import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { srcSetFor } from "@/lib/responsive";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionReveal from "@/components/SectionReveal";
import CTAStrip from "@/components/CTAStrip";
import WaveDivider from "@/components/WaveDivider";
import { useGalleryItems } from "@/hooks/useGalleryItems";
import type { GalleryItem } from "@/hooks/galleryTypes";

const defaultCategories = ["Hardwood", "Ceramic & Tile", "Laminate"];

function getAllImages(items: GalleryItem[]) {
  const images: { img: string; alt: string; caption: string; category: string }[] = [];
  for (const item of items) {
    if (item.type === "single") {
      images.push({ img: item.img, alt: item.alt, caption: item.caption, category: item.category });
    } else if (item.type === "beforeAfter") {
      images.push({ img: item.before.img, alt: item.before.alt, caption: item.before.caption, category: item.category });
      images.push({ img: item.after.img, alt: item.after.alt, caption: item.after.caption, category: item.category });
    } else if (item.type === "paired") {
      for (const photo of item.photos) {
        images.push(photo);
      }
    }
  }
  return images;
}

const OurWork = () => {
  const { items, categories: sanityCategories, isLoading } = useGalleryItems();
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const categories = ["All", ...(sanityCategories.length > 0 ? sanityCategories : defaultCategories)];
  const allImages = getAllImages(items);
  const filtered = activeCategory === "All" ? allImages : allImages.filter((img) => img.category === activeCategory);

  const openLightbox = useCallback((index: number) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % filtered.length : null));
  }, [filtered.length]);
  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + filtered.length) % filtered.length : null));
  }, [filtered.length]);

  return (
    <Layout>
      <SEOHead
        title="Our Work — Gallery | JTB Floors"
        description="Browse our gallery of hardwood, ceramic, laminate, tile & marble flooring projects. Before & after photos from JTB Floors in Barrington, IL."
      />

      {/* Hero */}
      <section className="pt-28 pb-12 lg:pt-36 lg:pb-16 bg-muted">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <SectionReveal>
            <h1 className="text-4xl md:text-5xl font-heading font-black mb-4">Our Work</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Browse real projects completed by JTB Floors — hardwood, ceramic, laminate, tile & marble installations across Barrington, IL and surrounding areas.
            </p>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* Category Filter + Gallery */}
      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="sr-only">Project Gallery</h2>
          {/* Category Tabs */}
          <SectionReveal>
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 border ${
                    activeCategory === cat
                      ? "bg-primary text-primary-foreground border-primary shadow-md"
                      : "bg-card text-foreground/70 border-border hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </SectionReveal>

          {/* Loading */}
          {isLoading && (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Grid */}
          {!isLoading && (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <AnimatePresence mode="popLayout">
                {filtered.map((image, i) => (
                  <motion.div
                    key={image.img + image.caption}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: i * 0.03 }}
                    className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer border border-border bg-muted"
                    onClick={() => openLightbox(i)}
                  >
                    <img
                      src={image.img}
                      srcSet={srcSetFor(image.img)}
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      alt={image.alt}
                      width={800}
                      height={600}
                      decoding="async"
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <p className="text-card text-sm font-medium">{image.caption}</p>
                      <span className="text-card/70 text-xs">{image.category}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          {!isLoading && filtered.length === 0 && (
            <p className="text-center text-muted-foreground py-16">No photos in this category yet.</p>
          )}
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--secondary))" />
      <CTAStrip />

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && filtered[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-foreground/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-card hover:text-primary transition-colors z-10"
              aria-label="Close lightbox"
            >
              <X className="w-8 h-8" />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-card hover:text-primary transition-colors z-10"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-card hover:text-primary transition-colors z-10"
              aria-label="Next image"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-w-5xl max-h-[85vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filtered[lightboxIndex].img}
                alt={filtered[lightboxIndex].alt}
                className="w-full h-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="text-center mt-3">
                <p className="text-card font-medium">{filtered[lightboxIndex].caption}</p>
                <p className="text-card/60 text-sm">{filtered[lightboxIndex].category}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default OurWork;
